import { useRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickOutside } from 'utils/useClickOutside';
import { useAnchorPosition } from './useAnchorPosition';
import { useNotificationsPanelAnimation } from './useNotificationsPanelAnimation';
import { IUsePanelData, IUsePanelProps } from '../types';

export const usePanel = ({
  anchorRef,
  open,
  onClose,
  buttonRef,
  notificationsList,
}: IUsePanelProps): IUsePanelData => {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);

  const position = useAnchorPosition(anchorRef, open);
  const animation = useNotificationsPanelAnimation(open);

  const [selectedTab, setSelectedTab] = useState(0);

  useClickOutside(panelRef, onClose, open, [buttonRef]);

  useEffect(() => {
    if (!open) return;

    const handleScroll = (event: Event) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target)) return;
      onClose();
    };

    window.addEventListener('scroll', handleScroll, true);
    // eslint-disable-next-line consistent-return
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [open, onClose]);

  const filteredNotifications = useMemo(() => {
    if (selectedTab === 0) return notificationsList;
    return notificationsList.filter((notification) => !notification.is_read);
  }, [notificationsList, selectedTab]);

  return {
    t,
    panelRef,
    position,
    animation,
    filteredNotifications,
    selectedTab,
    setSelectedTab,
  };
};
